import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';

interface CompactCalendarProps {
  selectedDate?: Date | null;
  onDateSelect?: (date: Date) => void;
  className?: string;
  disabledDates?: (date: Date) => boolean;
}

const CompactCalendar: React.FC<CompactCalendarProps> = ({
  selectedDate = null,
  onDateSelect = () => {},
  className,
  disabledDates,
}) => {
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [showYearPicker, setShowYearPicker] = React.useState(false);
  const [showMonthPicker, setShowMonthPicker] = React.useState(false);

  // Arabic month names
  const months = [
    'يناير',
    'فبراير',
    'مارس',
    'أبريل',
    'مايو',
    'يونيو',
    'يوليو',
    'أغسطس',
    'سبتمبر',
    'أكتوبر',
    'نوفمبر',
    'ديسمبر',
  ];

  // Arabic day names (Saturday first)
  const days = ['س', 'ح', 'ن', 'ث', 'ر', 'خ', 'ج'];

  // Get days in month
  const getDaysInMonth = (date: Date): (Date | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Adjust for Saturday start
    let startDay = firstDay.getDay();
    startDay = startDay === 6 ? 0 : startDay + 1;

    const monthDays: (Date | null)[] = [];

    // Empty cells
    for (let i = 0; i < startDay; i++) {
      monthDays.push(null);
    }

    // Month days
    for (let i = 1; i <= daysInMonth; i++) {
      monthDays.push(new Date(year, month, i));
    }

    return monthDays;
  };

  const monthDays = getDaysInMonth(currentMonth);

  // Navigation
  const navigateMonth = (direction: number) => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction),
    );
  };

  const selectMonth = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex));
    setShowMonthPicker(false);
  };

  const selectYear = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth()));
    setShowYearPicker(false);
  };

  // Check date states
  const isToday = (date: Date | null): boolean => {
    if (!date || !(date instanceof Date)) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date | null): boolean => {
    if (!date || !(date instanceof Date)) return false;
    if (!selectedDate || !(selectedDate instanceof Date)) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isDisabled = (date: Date | null): boolean => {
    if (!date || !disabledDates) return false;
    return disabledDates(date);
  };

  // Generate year range
  const currentYear = currentMonth.getFullYear();
  const startYear = currentYear - 50;
  const years = Array.from({ length: 101 }, (_, i) => startYear + i);

  return (
    <div
      className={cn(
        'relative w-72 rounded-lg border bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900',
        className,
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={() => navigateMonth(-1)}
          className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMonthPicker(!showMonthPicker)}
            className="rounded px-2 py-1 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {months[currentMonth.getMonth()]}
          </button>
          <button
            onClick={() => setShowYearPicker(!showYearPicker)}
            className="rounded px-2 py-1 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {currentMonth.getFullYear()}
          </button>
        </div>

        <button
          onClick={() => navigateMonth(1)}
          className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      {/* Month Picker */}
      {showMonthPicker && (
        <div className="absolute left-0 right-0 top-12 z-20 rounded-lg border bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <div className="grid grid-cols-3 gap-1">
            {months.map((month, index) => (
              <button
                key={index}
                onClick={() => selectMonth(index)}
                className={cn(
                  'rounded px-2 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-800',
                  currentMonth.getMonth() === index &&
                    'bg-blue-500 text-white hover:bg-blue-600',
                )}
              >
                {month}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Year Picker */}
      {showYearPicker && (
        <div className="absolute left-0 right-0 top-12 z-20 max-h-64 overflow-y-auto rounded-lg border bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <div className="grid grid-cols-4 gap-1">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => selectYear(year)}
                className={cn(
                  'rounded px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-800',
                  currentMonth.getFullYear() === year &&
                    'bg-blue-500 text-white hover:bg-blue-600',
                )}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Days of week */}
      <div className="mb-2 grid grid-cols-7 gap-1">
        {days.map((day, index) => (
          <div
            key={index}
            className="text-center text-xs font-medium text-gray-500 dark:text-gray-400"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {monthDays.map((date, index) => (
          <div key={index} className="aspect-square">
            {date && (
              <button
                onClick={() => !isDisabled(date) && onDateSelect(date)}
                disabled={isDisabled(date)}
                className={cn(
                  'flex h-full w-full items-center justify-center rounded text-xs transition-colors',
                  isDisabled(date) && 'cursor-not-allowed opacity-50',
                  isSelected(date) &&
                    'bg-blue-500 text-white hover:bg-blue-600',
                  isToday(date) &&
                    !isSelected(date) &&
                    'bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400',
                  !isSelected(date) &&
                    !isToday(date) &&
                    !isDisabled(date) &&
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                )}
              >
                {date.getDate()}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Today button */}
      <div className="mt-3 flex justify-center">
        <button
          onClick={() => {
            const today = new Date();
            setCurrentMonth(today);
            onDateSelect(today);
          }}
          className="rounded px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
        >
          اليوم
        </button>
      </div>
    </div>
  );
};

export default CompactCalendar;
export { CompactCalendar as Calendar };
